<template>
  <div class="tag-mgmt">
    <div class="tag-mgmt__shared-hint">Tags are shared across Intake and Prior Auth.</div>
    <div ref="scrollEl" class="tag-mgmt__scroll">
      <div v-if="!tags.length && !adding" class="tag-mgmt__empty">No tags yet. Click "+ Add Tag" below.</div>
      <div
        v-for="(tag, index) in tags"
        :key="tag.key + index"
        class="tag-mgmt__row"
        @mouseenter="hoverIndex = index"
        @mouseleave="hoverIndex = -1">
        <v-menu :close-on-content-click="true">
          <template #activator="{ props: menuProps }">
            <button v-bind="menuProps" class="tag-mgmt__dot tag-mgmt__dot--clickable" type="button">
              <span class="tag-mgmt__dot-inner" :style="{ background: TAG_COLORS[tag.color] || TAG_COLORS.grey }" />
            </button>
          </template>
          <div class="tag-mgmt__palette">
            <button
              v-for="(hex, name) in TAG_COLORS"
              :key="name"
              type="button"
              class="tag-mgmt__palette-swatch"
              :class="{ 'tag-mgmt__palette-swatch--active': tag.color === name }"
              :style="{ background: hex }"
              @click="setColor(tag, name)" />
          </div>
        </v-menu>
        <input
          v-model="tag.label"
          class="tag-mgmt__input"
          placeholder="Tag label"
          @input="tag.dirty = true"
          @blur="autoKey(tag)" />
        <v-btn
          icon="mdi-close"
          variant="text"
          size="x-small"
          class="tag-mgmt__delete"
          :class="{ 'tag-mgmt__delete--visible': hoverIndex === index }"
          @click="removeTag(index)" />
      </div>

      <!-- Inline add row -->
      <div v-if="adding" class="tag-mgmt__row tag-mgmt__row--adding">
        <span class="tag-mgmt__dot">
          <span class="tag-mgmt__dot-inner" :style="{ background: TAG_COLORS[newTag.color] || TAG_COLORS.grey }" />
        </span>
        <input
          ref="newLabelInput"
          v-model="newTag.label"
          class="tag-mgmt__input"
          placeholder="New tag label"
          @keyup.enter="confirmAdd"
          @keyup.escape="cancelAdd"
          @blur="confirmAdd" />
      </div>

      <button type="button" class="tag-mgmt__add-btn" :disabled="!loaded" @click="startAdd">+ Add Tag</button>
    </div>

    <!-- Save bar -->
    <div class="tag-mgmt__save-bar">
      <v-btn variant="text" size="small" @click="resetToDefaults">Reset to Defaults</v-btn>
      <transition name="tag-mgmt-fade">
        <span v-if="error" class="tag-mgmt__msg tag-mgmt__msg--error">{{ error }}</span>
        <span v-else-if="successMsg" class="tag-mgmt__msg tag-mgmt__msg--success">{{ successMsg }}</span>
      </transition>
      <v-spacer />
      <v-btn variant="flat" size="small" color="primary" :loading="saving" :disabled="!hasChanges" @click="save">
        Save
      </v-btn>
    </div>
  </div>
</template>

<script setup>
import { TAG_COLORS } from "@/constants/tagColors";
import { ref, computed, nextTick } from "vue";
import { useRequirementsStore } from "@/stores";
import { settingsService } from "@/services/settingsService";
import { useSettingsSaveFlow } from "@/composables/useSettingsSaveFlow";
import { sortTagDefs } from "@/utils/tags";

const requirementsStore = useRequirementsStore();
const { saving, error, successMsg, run: runSave } = useSettingsSaveFlow();
const loaded = ref(false);
const adding = ref(false);
const hoverIndex = ref(-1);
const newLabelInput = ref(null);
const scrollEl = ref(null);
const newTag = ref({ label: "", color: "blue", key: "" });

const tags = ref([]);
const initialTags = ref([]);

const hasChanges = computed(() => {
  const current = tags.value.map(({ key, label, color }) => ({ key, label, color }));
  return JSON.stringify(current) !== JSON.stringify(initialTags.value);
});

function labelToKey(label) {
  return label
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_|_$/g, "");
}

function autoKey(tag) {
  if (tag.dirty && tag.label) {
    tag.key = labelToKey(tag.label);
  }
}

function setColor(tag, color) {
  tag.color = color;
  tag.dirty = true;
}

function startAdd() {
  if (adding.value) {
    nextTick(() => newLabelInput.value?.focus());
    return;
  }
  adding.value = true;
  newTag.value = { label: "", color: "blue", key: "" };
  nextTick(() => newLabelInput.value?.focus());
}

function confirmAdd() {
  if (!adding.value) return;
  const label = newTag.value.label.trim();
  if (!label) {
    adding.value = false;
    return;
  }
  const key = labelToKey(label);
  if (!key) {
    adding.value = false;
    return;
  }

  if (tags.value.some((t) => t.key === key)) {
    error.value = `Tag "${key}" already exists.`;
    adding.value = false;
    newTag.value = { label: "", color: "blue", key: "" };
    return;
  }

  tags.value = sortTagDefs([...tags.value, { key, label, color: newTag.value.color, dirty: true }]);
  adding.value = false;
  newTag.value = { label: "", color: "blue", key: "" };
  error.value = "";
  const insertedAt = tags.value.findIndex((t) => t.key === key);
  nextTick(() => scrollEl.value?.querySelectorAll(".tag-mgmt__row")[insertedAt]?.scrollIntoView({ block: "nearest" }));
}

function cancelAdd() {
  adding.value = false;
  newTag.value = { label: "", color: "blue", key: "" };
}

function removeTag(index) {
  tags.value.splice(index, 1);
}

async function save() {
  if (adding.value) {
    confirmAdd();
    await nextTick();
  }
  await runSave(
    async () => {
      const current = tags.value.map(({ key, label, color }) => ({ key, label, color }));
      const response = await settingsService.updateCustomTags(current);
      if (!Array.isArray(response?.tags) || response.tags.length !== current.length) {
        throw new Error("Some tags were rejected by the server. Please review and retry.");
      }
      await requirementsStore.refreshCurrentUserClientConfig();
      await loadTags();
    },
    { successText: "Tags saved.", errorText: "Failed to save tags." }
  );
}

async function resetToDefaults() {
  await runSave(
    async () => {
      await settingsService.resetCustomTags();
      await requirementsStore.refreshCurrentUserClientConfig();
      await loadTags();
    },
    { successText: "Reset to defaults.", errorText: "Failed to reset tags." }
  );
}

async function loadTags() {
  try {
    const data = await settingsService.getCustomTags();
    const shared = sortTagDefs(data.intake).map((t) => ({ ...t, dirty: false }));
    tags.value = shared;
    initialTags.value = shared.map(({ key, label, color }) => ({ key, label, color }));
    loaded.value = true;
  } catch {
    error.value = "Failed to load tags.";
  }
}

loadTags();
</script>

<style lang="scss" scoped>
@import "@/styles/_variables.scss";

.tag-mgmt {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.tag-mgmt__shared-hint {
  flex-shrink: 0;
  font-family: $lh_font_family_dm_sans;
  font-size: 12px;
  color: $lh_color_blue_gray;
  padding: 0 16px 10px;
}

.tag-mgmt__scroll {
  flex: 1;
  padding: 0 4px;
}

.tag-mgmt__row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 12px;
  border-radius: 8px;
  transition: background-color $lh_transition_fast;

  &:hover {
    background: $lh_color_snowy_white;
  }
}

.tag-mgmt__row--adding {
  background: $lh_color_snowy_white;
}

.tag-mgmt__dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.tag-mgmt__dot--clickable {
  cursor: pointer;
  border: none;
  background: transparent;
  padding: 0;
}

.tag-mgmt__dot-inner {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  display: block;
}

.tag-mgmt__input {
  flex: 1;
  font-family: $lh_font_family_dm_sans;
  font-size: 14px;
  color: $lh_color_navy_dark;
  border: 1px solid transparent;
  border-radius: 6px;
  padding: 4px 8px;
  outline: none;
  background: transparent;
  transition: border-color $lh_transition_fast;

  &:focus {
    border-color: $lh_color_blue;
  }

  &:hover:not(:focus) {
    border-color: $lh_dash_border;
  }
}

.tag-mgmt__delete {
  color: $lh_color_blue_gray !important;
  opacity: 0;
  transition: opacity $lh_transition_fast;

  &--visible {
    opacity: 0.6;
  }

  &--visible:hover {
    color: $lh_color_red !important;
    opacity: 1;
  }
}

.tag-mgmt__palette {
  display: grid;
  grid-template-columns: repeat(8, 10px);
  gap: 8px;
  padding: 10px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
}

.tag-mgmt__palette-swatch {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  border: 2px solid transparent;
  cursor: pointer;
  padding: 0;
  transition: border-color $lh_transition_fast;
}

.tag-mgmt__palette-swatch--active {
  border-color: $lh_color_navy_dark;
  box-shadow: 0 0 0 2px white, 0 0 0 4px currentColor;
}

.tag-mgmt__add-btn {
  font-family: $lh_font_family_dm_sans;
  font-size: 14px;
  color: $lh_color_blue_gray;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 8px 12px;
  border-radius: 8px;
  transition: color $lh_transition_fast, background-color $lh_transition_fast;

  &:hover {
    color: $lh_color_blue;
    background: $lh_color_snowy_white;
  }
}

.tag-mgmt__save-bar {
  display: flex;
  align-items: center;
  padding: 12px 0;
  border-top: 1px solid $lh_dash_border;
  flex-shrink: 0;
  position: sticky;
  bottom: 0;
  background: white;
  z-index: 1;
}

.tag-mgmt__msg {
  font-family: $lh_font_family_dm_sans;
  font-size: 12px;
}

.tag-mgmt__msg--error {
  color: $lh_color_red;
}

.tag-mgmt__msg--success {
  color: $lh_color_green;
}

.tag-mgmt__empty {
  font-family: $lh_font_family_dm_sans;
  font-size: 14px;
  color: $lh_color_blue_gray;
  padding: 12px;
}
</style>
